import { ShieldCheck, ExternalLink } from "lucide-react";

/**
 * VisaSponsorVerifiedChip
 * -----------------------------------------------------------------------------
 * Renders a "Verified sponsor · {authority}" pill in place of our old
 * editorial "Visa sponsor OK" chip. Backed by the government-maintained
 * register data seeded via `data/visa_sponsors.py`. Absence of the
 * `sponsor` prop means we haven't verified — callers should render the
 * legacy editorial chip in that case.
 *
 * Sizes:
 *   sm — 20px height, used in Advanced Results card rows
 *   md — 28px height, used in the DegreeDetail hero
 */
export default function VisaSponsorVerifiedChip({ sponsor, size = "sm", testid }) {
  if (!sponsor?.authority) return null;
  const isMd = size === "md";
  // Prefer a per-provider deep link when the government register
  // supports one (currently AU CRICOS, US SEVIS). Fall back to the
  // register root URL. This is what turns the chip into evidence that
  // a specific provider is on-register, not just "some entry exists".
  const href = sponsor.provider_url || sponsor.source_url;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={testid || "visa-sponsor-verified-chip"}
      title={`${sponsor.authority} · ${sponsor.register} · ref ${sponsor.sponsor_ref} · checked ${sponsor.checked_at}`}
      className={`inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 hover:border-emerald-300 transition-colors ${
        isMd
          ? "text-[12px] px-2.5 py-1"
          : "text-[11px] px-2 py-0.5"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <ShieldCheck className={isMd ? "h-3.5 w-3.5" : "h-3 w-3"} aria-hidden />
      <span className="font-medium">Verified sponsor</span>
      <span className="text-emerald-700 font-underground-sc tracking-wider text-[9px]">
        {sponsor.country === "USA" ? "SEVP" :
         sponsor.country === "UK" ? "UK Home Office" :
         sponsor.country === "Australia" ? "CRICOS" :
         sponsor.country === "Canada" ? "DLI" :
         sponsor.country === "Netherlands" ? "Nuffic" :
         sponsor.country === "Ireland" ? "ILEP" :
         sponsor.country === "Germany" ? "Anabin" : sponsor.country}
      </span>
      <ExternalLink className={isMd ? "h-3 w-3 text-emerald-700" : "h-2.5 w-2.5 text-emerald-700"} aria-hidden />
    </a>
  );
}
